import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/config/supabase'
import { usePortalStore } from '@/stores/portal.store'
import { 
    Loader2, Shield, ArrowRight, MessageCircle, KeyRound, AlertCircle
} from 'lucide-react'

/* ================================================================
   Helper — CPF mask
   ================================================================ */
function maskCpf(value: string) {
    const d = value.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 3) return d
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

export function PortalLoginPage() {
    const navigate = useNavigate()
    const { setAssociado, isAuthenticated } = usePortalStore()

    const [step, setStep] = useState<'identify' | 'otp'>('identify')
    const [cpf, setCpf] = useState('')
    const [cpfError, setCpfError] = useState('')
    const [loading, setLoading] = useState(false)

    // OTP
    const [otp, setOtp] = useState('')
    const [loginError, setLoginError] = useState('')
    const [maskedPhone, setMaskedPhone] = useState('')

    // Timer de reenvio
    const [resendTimer, setResendTimer] = useState(0)

    useEffect(() => {
        let interval: any
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(t => t - 1)
            }, 1000)
        }
        return () => clearInterval(interval)
    }, [resendTimer])

    // Se já estiver logado, vai direto pro dashboard
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/portal')
        }
    }, [isAuthenticated, navigate])

    /* ----------------------------------------------------------------
       Handlers
    ---------------------------------------------------------------- */
    const handleIdentify = async (isResend = false) => {
        const cleanCpf = cpf.replace(/\D/g, '')
        if (cleanCpf.length !== 11) {
            setCpfError('Informe um CPF válido com 11 dígitos')
            return
        }
        setCpfError('')
        setLoading(true)
        setLoginError('')

        try {
            const { data, error } = await supabase.functions.invoke('portal-auth', {
                body: { action: 'send', cpf: cleanCpf }
            })

            if (error) {
                const errorMsg = await error.context?.json().catch(() => null)
                throw new Error(errorMsg?.error || 'Erro ao validar o CPF.')
            }
            if (data?.error) throw new Error(data.error)

            // Sucesso ao enviar
            if (data.masked_phone) {
                setMaskedPhone(data.masked_phone)
            }
            
            setStep('otp')
            setResendTimer(60) // Inicia contagem de 60s
            
            if (isResend) {
                setLoginError('[OK] Código reenviado com sucesso!')
            }

            // Dev helper
            if (import.meta.env.DEV && data?.dev_code) {
                setLoginError(`[DEV] Código: ${data.dev_code}`)
            }
        } catch (err: any) {
            console.error('Portal Auth Error:', err)
            const msg = err.message || 'Erro ao validar CPF.'
            if (isResend) setLoginError(msg)
            else setCpfError(msg)
        } finally {
            setLoading(false)
        }
    }

    const handleVerify = async () => {
        if (otp.length !== 6) return
        setLoading(true)
        setLoginError('')

        try {
            const cleanCpf = cpf.replace(/\D/g, '')
            const { data, error } = await supabase.functions.invoke('portal-auth', {
                body: { action: 'verify', cpf: cleanCpf, codigo: otp }
            })

            if (error) {
                const errorMsg = await error.context?.json().catch(() => null)
                throw new Error(errorMsg?.error || 'Código inválido ou expirado.')
            }

            if (data?.error) throw new Error(data.error)

            // Login bem sucedido!
            if (data.associado) {
                setAssociado(data.associado, data.cpf)
                navigate('/portal')
            }
        } catch (err: any) {
            console.error('Verify Error:', err)
            setLoginError(err.message || 'Código incorreto.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col bg-white text-[#061C37] font-['Poppins',sans-serif]">
            {/* Google Fonts - Poppins */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
                body { font-family: 'Poppins', sans-serif; }
            `}</style>

            {/* Header / Logo Area */}
            <header className="bg-[#003E8A] py-12 px-6 flex flex-col items-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-20 -mt-20 blur-3xl opacity-50" />
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#00A344]/10 rounded-full -ml-40 -mb-40 blur-3xl" />
                
                <div className="relative z-10 flex flex-col items-center">
                    <img 
                        src="/logobranca.png" 
                        alt="Multiveicular" 
                        className="h-16 w-auto object-contain mb-8"
                    />
                    <h1 className="text-white text-2xl font-bold tracking-tight text-center uppercase">
                        Portal do Associado
                    </h1>
                    <p className="text-white/70 text-sm mt-2 font-medium">Sua proteção em um só lugar</p>
                </div>
            </header>

            <main className="flex-1 flex flex-col items-center -mt-12 px-6 relative z-20 pb-16">
                <div className="w-full max-w-sm bg-white border border-gray-100 p-10 rounded-[2.5rem] shadow-[0_30px_60px_rgba(0,62,138,0.15)] space-y-8">
                    {step === 'identify' ? (
                        <div className="space-y-8">
                            <div className="space-y-2 text-center">
                                <h2 className="text-2xl font-bold text-[#061C37]">Acesse sua conta</h2>
                                <p className="text-sm text-gray-400">Informe seu CPF para verificarmos seu cadastro oficial</p>
                            </div>
                            
                            <div className="space-y-3">
                                <label className="text-xs font-bold text-[#061C37]/40 uppercase tracking-[0.15em] ml-1">CPF do Titular</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={cpf}
                                    onChange={e => setCpf(maskCpf(e.target.value))}
                                    placeholder="000.000.000-00"
                                    className="w-full bg-[#f8fafc] border border-[#e2e8f0] rounded-[1.25rem] px-6 py-5 text-xl font-bold text-[#061C37] placeholder:text-gray-300 focus:outline-none focus:ring-4 focus:ring-[#003E8A]/5 focus:border-[#003E8A] transition-all"
                                    onKeyDown={e => e.key === 'Enter' && handleIdentify()}
                                />
                                {cpfError && (
                                    <p className="text-red-500 text-xs font-semibold flex items-center gap-1.5 mt-2 ml-1">
                                        <AlertCircle size={14} /> {cpfError}
                                    </p>
                                )}
                            </div>

                            <button
                                onClick={() => handleIdentify(false)}
                                disabled={loading}
                                className="w-full bg-[#003E8A] hover:bg-[#002B61] disabled:opacity-50 text-white font-bold py-5 rounded-full shadow-xl shadow-[#003E8A]/20 flex items-center justify-center gap-3 transition-all active:scale-[0.97] text-lg"
                            >
                                {loading ? <Loader2 className="animate-spin" size={24} /> : <>Verificar Cadastro <ArrowRight size={24} /></>}
                            </button>

                            <p className="text-center text-[12px] text-gray-400 leading-relaxed px-6 italic">
                                Por segurança, enviaremos um código exclusivo para seu email e celular cadastrados.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            <div className="space-y-3 text-center">
                                <div className="w-20 h-20 bg-[#00A344]/10 rounded-full flex items-center justify-center mx-auto mb-2 animate-pulse">
                                    <MessageCircle className="text-[#00A344]" size={40} />
                                </div>
                                <h2 className="text-2xl font-bold text-[#061C37]">Código enviado!</h2>
                                <p className="text-sm text-gray-400 leading-relaxed px-2">
                                    Verifique seu email e celular cadastrados.<br/>
                                    <span className="text-[#061C37] font-bold text-base">{maskedPhone}</span>
                                </p>
                            </div>

                            <div className="space-y-3">
                                <label className="text-xs font-bold text-[#061C37]/40 uppercase tracking-[0.15em] ml-1">Código de Segurança</label>
                                <div className="relative">
                                    <KeyRound className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300" size={24} />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        value={otp}
                                        onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        placeholder="000000"
                                        className="w-full bg-[#f8fafc] border border-[#e2e8f0] rounded-[1.25rem] pl-16 pr-6 py-5 text-3xl font-bold tracking-[0.6em] text-[#061C37] placeholder:text-gray-200 focus:outline-none focus:ring-4 focus:ring-[#003E8A]/5 focus:border-[#003E8A] transition-all text-center"
                                        onKeyDown={e => e.key === 'Enter' && handleVerify()}
                                    />
                                </div>
                                {loginError && (
                                    <p className={`text-sm font-semibold flex items-center gap-1.5 mt-2 ml-1 ${loginError.includes('[DEV]') || loginError.includes('[OK]') ? 'text-amber-600' : 'text-red-500'}`}>
                                        <AlertCircle size={16} /> {loginError}
                                    </p>
                                )}
                            </div>

                            <button
                                onClick={handleVerify}
                                disabled={loading || otp.length !== 6}
                                className="w-full bg-[#00A344] hover:bg-[#008A39] disabled:opacity-50 text-white font-bold py-5 rounded-full shadow-xl shadow-[#00A344]/30 flex items-center justify-center gap-3 transition-all active:scale-[0.97] text-lg"
                            >
                                {loading ? <Loader2 className="animate-spin" size={24} /> : <>Acessar Portal</>}
                            </button>

                            <div className="flex flex-col gap-4 text-center pt-2">
                                <button
                                    onClick={() => handleIdentify(true)}
                                    disabled={loading || resendTimer > 0}
                                    className="text-sm text-[#003E8A] font-bold hover:text-[#002B61] disabled:opacity-40 transition-all flex items-center justify-center gap-2"
                                >
                                    {resendTimer > 0 ? (
                                        <span className="flex items-center gap-2 bg-gray-50 px-4 py-2 rounded-full text-gray-400 font-medium">
                                            <Loader2 size={14} className="animate-spin" /> Reenviar em {resendTimer}s
                                        </span>
                                    ) : 'Reenviar código agora'}
                                </button>

                                <button
                                    onClick={() => { setStep('identify'); setOtp(''); setLoginError('') }}
                                    className="text-xs text-gray-400 hover:text-[#003E8A] font-bold uppercase tracking-widest transition-colors"
                                >
                                    Alterar CPF
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Support Link */}
                <div className="mt-12">
                    <a href="https://wa.me/5549991320199" target="_blank" rel="noreferrer" className="flex items-center gap-4 px-8 py-4 bg-white border border-gray-100 rounded-full shadow-xl text-base text-[#061C37] font-bold hover:shadow-2xl transition-all hover:-translate-y-1">
                        <div className="w-10 h-10 bg-[#00A344] rounded-full flex items-center justify-center text-white shadow-lg shadow-[#00A344]/20">
                            <MessageCircle size={20} fill="currentColor" />
                        </div>
                        Suporte via WhatsApp
                    </a>
                </div>
            </main>

            <footer className="p-10 text-center border-t border-gray-50 mt-auto bg-gray-50/30">
                <p className="text-sm text-gray-400 font-bold uppercase tracking-[0.2em] mb-2">
                    ACSM Multiveicular
                </p>
                <p className="text-xs text-gray-300 font-medium">
                    &copy; {new Date().getFullYear()} &middot; Proteção Premiada &middot; Todos os direitos reservados.
                </p>
            </footer>
        </div>
    )
}
